import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./i18n";
import "./App.css";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import InviteAccept from "./pages/InviteAccept";
import BillingResult from "./pages/BillingResult";
import LegalPage from "./pages/legal/LegalPage";
import Dashboard from "./pages/admin/Dashboard";
import Receipts from "./pages/admin/Receipts";
import Duplicates from "./pages/admin/Duplicates";
import Scan from "./pages/admin/Scan";
import Reports from "./pages/admin/Reports";
import Team from "./pages/admin/Team";
import AuditLog from "./pages/admin/AuditLog";
import SettingsPage from "./pages/admin/Settings";
import Billing from "./pages/admin/Billing";
import EmployeeScan from "./pages/employee/EmployeeScan";
import EmployeeReceipts from "./pages/employee/EmployeeReceipts";
import EmployeeRefunds from "./pages/employee/EmployeeRefunds";
import PayoutsOnly from "./components/PayoutsOnly";

const FullPageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

const homeFor = (role) => (role === "employee" ? "/me" : "/app/dashboard");

const RequireAuth = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
};

const RedirectIfAuthed = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return children;
};

const STAFF = ["admin", "accountant"];
const ADMIN = ["admin"];
const EMPLOYEE = ["employee"];

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Landing />} />
    <Route path="/legal/:doc" element={<LegalPage />} />
    <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
    <Route
      path="/login"
      element={
        <RedirectIfAuthed>
          <Login />
        </RedirectIfAuthed>
      }
    />
    <Route
      path="/signup"
      element={
        <RedirectIfAuthed>
          <Signup />
        </RedirectIfAuthed>
      }
    />
    <Route path="/invite/:token" element={<InviteAccept />} />

    <Route
      path="/app/dashboard"
      element={
        <RequireAuth roles={STAFF}>
          <Dashboard />
        </RequireAuth>
      }
    />
    <Route
      path="/app/receipts"
      element={
        <RequireAuth roles={STAFF}>
          <Receipts />
        </RequireAuth>
      }
    />
    <Route
      path="/app/duplicates"
      element={
        <RequireAuth roles={ADMIN}>
          <Duplicates />
        </RequireAuth>
      }
    />
    <Route
      path="/app/scan"
      element={
        <RequireAuth roles={ADMIN}>
          <Scan />
        </RequireAuth>
      }
    />
    <Route
      path="/app/reports"
      element={
        <RequireAuth roles={STAFF}>
          <Reports />
        </RequireAuth>
      }
    />
    <Route
      path="/app/team"
      element={
        <RequireAuth roles={ADMIN}>
          <Team />
        </RequireAuth>
      }
    />
    <Route
      path="/app/settings"
      element={
        <RequireAuth roles={ADMIN}>
          <SettingsPage />
        </RequireAuth>
      }
    />
    <Route
      path="/app/audit"
      element={
        <RequireAuth roles={ADMIN}>
          <AuditLog />
        </RequireAuth>
      }
    />
    <Route
      path="/app/billing"
      element={
        <RequireAuth roles={ADMIN}>
          <Billing />
        </RequireAuth>
      }
    />
    <Route path="/billing/success" element={<BillingResult />} />
    <Route path="/billing/cancel" element={<BillingResult cancelled />} />

    <Route
      path="/me"
      element={
        <RequireAuth roles={EMPLOYEE}>
          <EmployeeScan />
        </RequireAuth>
      }
    />
    <Route
      path="/me/receipts"
      element={
        <RequireAuth roles={EMPLOYEE}>
          <EmployeeReceipts />
        </RequireAuth>
      }
    />
    <Route
      path="/me/refunds"
      element={
        <RequireAuth roles={EMPLOYEE}>
          <PayoutsOnly>
            <EmployeeRefunds />
          </PayoutsOnly>
        </RequireAuth>
      }
    />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="bottom-right" closeButton duration={4000} />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
